/**
 * Haptic Feedback Utilities for Mobile Experience
 * Provides tactile feedback simulation for button presses and interactions
 */

export type HapticFeedbackType = 'light' | 'medium' | 'heavy' | 'success' | 'warning' | 'error';

/**
 * Triggers haptic feedback on supported devices
 * Falls back gracefully on unsupported browsers
 */
export const triggerHaptic = (type: HapticFeedbackType = 'light') => {
  // Check if Vibration API is supported
  if (!('vibrate' in navigator)) {
    return;
  }

  // Map feedback types to vibration patterns
  const patterns: Record<HapticFeedbackType, number | number[]> = {
    light: 10,
    medium: 20,
    heavy: 30,
    success: [10, 50, 10],
    warning: [20, 100, 20],
    error: [30, 100, 30, 100, 30],
  };

  try {
    navigator.vibrate(patterns[type]);
  } catch (error) {
    // Silently fail if vibration is not supported
    console.debug('Haptic feedback not available:', error);
  }
};

/**
 * Haptic feedback for button presses
 */
export const hapticButton = () => triggerHaptic('light');

/**
 * Haptic feedback for successful actions
 */
export const hapticSuccess = () => triggerHaptic('success');

/**
 * Haptic feedback for errors
 */
export const hapticError = () => triggerHaptic('error');

/**
 * Haptic feedback for warnings
 */
export const hapticWarning = () => triggerHaptic('warning');

/**
 * Haptic feedback for delete/destructive actions
 */
export const hapticDelete = () => triggerHaptic('heavy');

/**
 * Haptic feedback for selections/toggles
 */
export const hapticSelection = () => triggerHaptic('medium');